import { defineStore } from 'pinia'
import {
  getAllProductionTime,
  getImprintMethodProductionTime,
  getMaxQty,
  getMinQty,
  getTotalQty,
  getValidProductionTime,
  type ProductionTimeItem,
  useWindow
} from '../../composables'
import { useApp } from './app'
import { useProductSpec } from './productSpec'
import { calcDelivery } from '@/api/OrderCustomizeClient'
import { useImprintMethod } from './imprintMethod'

interface ProductionTimeState {
  allProductionTime: ProductionTimeItem[]
}

export const useProductionTime = defineStore('productionTime', {
  state: (): ProductionTimeState => {
    return {
      allProductionTime: []
    }
  },
  getters: {
    getAllProductionTime (): ProductionTimeItem[] {
      return this.allProductionTime
    },
    getCurrentProductionTime (): ProductionTimeItem | undefined {
      return this.allProductionTime.find((f) => f.selected)
    }
  },
  actions: {
    setAllProductionTime (values: ProductionTimeItem[]): void {
      this.allProductionTime.splice(0)
      this.allProductionTime.push(...values)

      const { productId } = useWindow()
      const appState = useApp()
      const productSpecListState = useProductSpec()

      const etaList = productSpecListState.getSelectedList
        .map((f) => {
          return Array.isArray(f.quantity)
            ? f.quantity.map((stock) => stock.stock?.eta)
            : [f.quantity.stock?.eta]
        })
        .flat(Infinity)
        .filter((f) => !!f)

      if (!appState.getIsFreeGift) {
        if (this.allProductionTime.length > 0) {
          calcDelivery({
            ProductID: productId,
            TransitTime: appState.transitTime,
            Eta: etaList.length > 0 ? etaList[0] : '',
            ProductionList: this.allProductionTime.map(
              (f) => f.productionTime
            )
          }).then((values: string[]) => {
            values.forEach((it: string, i: number) => {
              this.allProductionTime[i].title = it
            })
          })
        }
      }
    },

    setCurrentProductionTime (val: ProductionTimeItem): void {
      this.allProductionTime.forEach((f) => {
        f.selected = false
      })
      const currentProductionTime = this.allProductionTime.find(
        (f) => f.enabled && f.productionTime === val.productionTime
      )
      if (currentProductionTime) {
        currentProductionTime.selected = true
      }
    },
    updateProductionTime (): void {
      const appState = useApp()
      const {
        productId,
        locationGroupList,
        blankProductionTime
      } = useWindow()
      const productSpecListState = useProductSpec()
      const imprintMethodState = useImprintMethod()

      //临时记住之前所选的
      const _currentProductionTime = this.getCurrentProductionTime
      // 先计算所有production time
      this.allProductionTime.splice(0)
      this.allProductionTime.push(
        ...getAllProductionTime(appState.getBlankValue)
      )
      //如果之前所选的有，那就使用之前选中的数据
      if (_currentProductionTime) {
        this.allProductionTime.some(f => {
          if (f.productionTime === _currentProductionTime.productionTime) {
            f.selected = true;
            return true;
          }
          return false;
        })
      }

      // 判断最大数量和最小数量
      const totalQty = getTotalQty(productSpecListState.getSelectedList)

      // 获取当前所选的productiontime
      const imprintMethodProductionTime = getImprintMethodProductionTime(
        imprintMethodState.getSelectedList,
        locationGroupList,
        totalQty,
        appState.getCurrentPriceList
      )
      // 获取有效天
      const productionDays = getValidProductionTime(
        appState.getCurrentPriceList,
        appState.getBlankValue,
        blankProductionTime,
        imprintMethodProductionTime
      )

      // 计算当前可用的price list
      this.allProductionTime.forEach((it) => {
        // it.priceList = [...pri]
        if (productionDays[it.productionTime] || productionDays[it.rushDay]) {
          it.enabled = true
          it.priceList = productionDays[it.productionTime]
        } else {
          it.enabled = false
          it.priceList = []
        }
      })

      this.allProductionTime.forEach((pt) => {
        // 计算当前price 可用的
        const minQty = getMinQty(
          pt.priceList,
          imprintMethodState.getSelectedList,
          true
        )
        const maxQty = getMaxQty(pt.priceList)

        if (
          (minQty > 0 && totalQty < minQty) ||
          (maxQty > 0 && totalQty > maxQty)
        ) {
          pt.enabled = false
        }
      })

      const selectedProductionTime = this.getCurrentProductionTime
      // 默认选中第一个
      let firstProductionTime = this.allProductionTime.find(
        (f) =>
          f.enabled &&
          f.productionTime === selectedProductionTime?.productionTime
      )

      if (!firstProductionTime) {
        firstProductionTime = this.allProductionTime.find((f) => f.enabled)
      }

      if (appState.getIsFreeGift) {
        if (firstProductionTime) {
          this.setCurrentProductionTime(firstProductionTime)
          this.allProductionTime.forEach((f) => {
            if (firstProductionTime) {
              if (f.productionTime === firstProductionTime.productionTime) {
                f.selected = true
              } else {
                f.selected = false
              }
            }
          })
        }
        return
      }

      /**
       * 判断是否包含OOS
       */
      const hasOutOfStock = productSpecListState.getSelectedList
        .map((f) => {
          const data = Array.isArray(f.quantity) ? f.quantity : [f.quantity]
          return data
            .filter((f) => {
              // 没有库存，那就不是OOS
              if (!f.stock) return false
              // call for stock也认为是OOS
              if (f.stock.status === 'callforStock') return true
              // 如果是out of stock，那没有ETA才算是OOS
              if (f.stock.status === 'outofStock' && !f.stock.eta) return true
              return false
            })
            .map((stock) => stock.stock?.status)
        })
        .flat(Infinity)
        .some((f) => f?.includes('outofStock') || f?.includes('callforStock'))


      if (hasOutOfStock) {
        if (firstProductionTime) {
          this.allProductionTime.splice(0)
          firstProductionTime.title = 'To be determined'
          firstProductionTime.needCalcDelivery = false;
          this.allProductionTime.push(firstProductionTime)

          this.setCurrentProductionTime(firstProductionTime)
        }
      } else {
        if (firstProductionTime) {
          firstProductionTime.needCalcDelivery = true;
          
          this.setCurrentProductionTime(firstProductionTime)
          this.allProductionTime.forEach((f) => {
            if (firstProductionTime) {
              if (f.productionTime === firstProductionTime.productionTime) {
                f.selected = true
              } else {
                f.selected = false
              }
            }
          })

          const etaList = productSpecListState.getSelectedList
            .map((f) => {
              return Array.isArray(f.quantity)
                ? f.quantity.map((stock) => stock.stock?.eta)
                : [f.quantity.stock?.eta]
            })
            .flat(Infinity)
            .filter((f) => !!f)

          calcDelivery({
            ProductID: productId,
            TransitTime: appState.transitTime,
            Eta: etaList.length > 0 ? etaList[0] : '',
            ProductionList: this.allProductionTime.map(
              (f) => f.productionTime
            )
          }).then((values: string[]) => {
            values.forEach((it: string, i: number) => {
              this.allProductionTime[i].title = it
            })
          })
        }
      }
    }
  }
})
